import { Injectable }  from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { URLSearchParams } from '@angular/http';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/mergeMap';
import { forkJoin } from 'rxjs/observable/forkJoin';

import { ConfigService } from '../../../config/config.service';

import { AuthResourceHandler, TokenMode } from '../user/auth-resource-handler';

@Injectable()
export class Wso2ApiService {

    private storeApiUrl;
    private tokenApiUrl;
    private revokeApiUrl;
    private applicationName = 'api-subscriptions-app';

    constructor(
        configService: ConfigService,
        private http: HttpClient,
        authResourceHandler: AuthResourceHandler
    ) {
        this.storeApiUrl = configService.config.wso2StoreApiUrl;
        this.tokenApiUrl = configService.config.wso2TokenApiUrl;
        this.revokeApiUrl = configService.config.wso2RevokeApiUrl;
        authResourceHandler.addProtectedService({
            url: this.storeApiUrl,
            tokenMode: TokenMode.Header
        });
    }

    getOrCreateUserApplication() {
        return this.getUserApplicationId(this.applicationName).flatMap((appId: string) => {
            if (!appId) {
                return this.createUserApplication({name: this.applicationName}).flatMap((appId: string) => {
                    return this.generateApplicationKeys(appId).map((keys) => {
                        return {
                            id: appId,
                            isNew: true,
                            ...keys,
                        };
                    });
                });
            } else {
                return this.getOrCreateApplicationkeys(appId).map((keys) => {
                    return {
                        id: appId,
                        isNew: false,
                        ...keys
                    };
                });
            }

        });

    }

    refreshApiToken({consumerKey, consumerSecret, apiToken}) {
        return this.revokeApiToken({consumerKey, consumerSecret, apiToken}).flatMap(() => {
            return this.generateApiToken({consumerKey, consumerSecret});
        });
    }

    revokeApiToken({consumerKey, consumerSecret, apiToken}) {
        let params = new URLSearchParams();
        params.set('token', apiToken);

        let credentials = btoa(`${consumerKey}:${consumerSecret}`);

        return this.http.post(this.revokeApiUrl, params.toString(), {
            headers: {
                Authorization: `Basic ${credentials}`,
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });
    }

    generateApiToken({consumerKey, consumerSecret}) {

        let params = new URLSearchParams();
        params.set('grant_type', 'client_credentials');

        let credentials = btoa(`${consumerKey}:${consumerSecret}`);

        return this.http.post(this.tokenApiUrl, params.toString(), {
            headers: {
                Authorization: `Basic ${credentials}`,
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }).map((response: any) => {
            return {
                token: response.access_token,
                expiresIn: response.expires_in
            };
        });
    }

    getTokenApiUrl() {
        return this.tokenApiUrl;
    }

    getApisForTag(tag: string) {
        return this.http.get(`${this.storeApiUrl}/apis`, {
            params: {
                query: `tag:${tag}`,
                limit: '100'
            },
            responseType: 'json'
        }).map((response: any) => {
            return response.list;
        });
    }

    getSubscriptions(appId: string) {
        return this.http.get(`${this.storeApiUrl}/subscriptions`, {
            params: {
                applicationId: appId,
                limit: '100'
            }
        }).map((response: any) => {
            return response.list;
        });
    }

    subscribeToApisWithTags(appId: string, tags: string[]) {
        let requests = tags.map((tag) => {
            return this.subscribeToApisWithTag(appId, tag);
        });

        return forkJoin(requests);
    }

    subscribeToApisWithTag(appId: string, tag: string) {
        return this.getApisForTag(tag).flatMap((apis) => {
            return this.subscribeToApis(appId, apis.map(api => api.id));
        });
    }

    subscribeToApis(appId: string, apis: string[]) {

        let requests = apis.map((api) => {
            return this.subscribeToApi(appId, api);
        });

        return forkJoin(requests);
    }

    subscribeToApi(appId: string, apiId: string, tier: string = 'Unlimited') {
        return this.http.post(`${this.storeApiUrl}/subscriptions`, {
            applicationId: appId,
            apiId: apiId,
            throttlingPolicy: tier
        }).map((response) => {
            return {
                status: 200,
                data: response
            };
        })
        .catch((response) => {
            if (response.status === 403) {
                let message = response.error.description;
                let matches = message.match(/Only \[([^\]]*)\] Tiers are allowed\.$/);
                if (matches && matches[1]) {
                    let tiers = matches[1].split(',');
                    return this.subscribeToApi(appId, apiId, tiers[0]);
                }
            }
            return Observable.of(response.error);
        });
    }

    unsubscribeFromApis(apis: string[]) {

        let requests = apis.map((api) => {
            return this.unsubscribeFromApi(api);
        });

        return forkJoin(requests);
    }

    unsubscribeFromApi(apiId: string) {
        return this.http.delete(`${this.storeApiUrl}/subscriptions/${apiId}`)
        .catch((response) => {
            return Observable.of(response.error);
        });
    }

    protected getOrCreateApplicationkeys(appId) {
        return this.getApplicationKeys(appId).flatMap((keys) => {
            if (keys && keys.consumerKey) {
                return Observable.of(keys);
            } else {
                return this.generateApplicationKeys(appId);
            }
        });
    }

    protected getApplicationKeys(appId: string) {
        return this.http.get(`${this.storeApiUrl}/applications/${appId}/keys`).map((response: any) => {
            if (response.list && response.list.length) {
                let key = response.list[0];
                return {
                    consumerKey: key.consumerKey,
                    consumerSecret: key.consumerSecret,
                    apiToken: key.token ? key.token.accessToken : null
                };
            }
        });
    }

    protected generateApplicationKeys(appId: string) {
        return this.http.post(`${this.storeApiUrl}/applications/${appId}/generate-keys`, {
            validityTime: 3600,
            keyType: 'PRODUCTION'
        }).map((key: any) => {
            return {
                consumerKey: key.consumerKey,
                consumerSecret: key.consumerSecret,
                apiToken: key.token ? key.token.accessToken : null
            };
        });
    }

    protected getUserApplicationId(name: string) {
        return this.http.get(`${this.storeApiUrl}/applications`, {
            params: {
                query: name
            },
            responseType: 'json'
        }).map((response: any) => {
            return response.list.length ? response.list[0].applicationId : null;
        });
    }

    protected createUserApplication({name, tier = 'Unlimited', description = 'User API subscriptions application' }) {

        return this.http.post(`${this.storeApiUrl}/applications`, {
            throttlingPolicy: tier,
            description: description,
            tokenType: 'OAUTH',
            name: name
        }, {
            responseType: 'json'
        }).map((appData: any) => {
            return appData.applicationId;
        });
    }
}



// WEBPACK FOOTER //
// ./src/main/services/api/wso2-api-service.ts